import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import rateCodeService from '../services/rateCodeService';
import './ProfileIATAPage.css';

const RateCodesPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [search, setSearch] = useState('');
    const [rateCodes, setRateCodes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => { loadRateCodes(); }, [ppId]); // eslint-disable-line

    const loadRateCodes = async () => {
        setLoading(true);
        try {
            const res = await rateCodeService.list(ppId);
            setRateCodes(res.data?.rateCodes || []);
        } catch (err) {
            console.error('Failed to load rate codes', err);
        } finally {
            setLoading(false);
        }
    };

    const filtered = rateCodes.filter(r =>
        !search ||
        (r.rate_code || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.rate_code_name || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this rate code?')) return;
        try {
            await rateCodeService.remove(ppId, id);
            setRateCodes(prev => prev.filter(r => r.id !== id));
        } catch (err) {
            console.error('Failed to delete rate code', err);
        }
    };

    const RateCodeIcon = () => (
        <div className="pi-avatar" style={{ background: '#dcfce7' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#16a34a" strokeWidth="1.75"/>
                <path d="M2 10h20" stroke="#16a34a" strokeWidth="1.75"/>
                <path d="M6 15h4" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
        </div>
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back to Payment Porter
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Rate Code Management</h1>
                        <p className="pi-subtitle">Configure rate codes for automated reservation filtering and payment processing</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => navigate(`/apps/payment-porter/${ppId}/rate-codes/new`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Rate Code
                    </button>
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search rate codes by code or name..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="pi-list-panel">
                {!loading && filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M2 10h20" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M6 15h4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No rate codes match your search' : 'No rate codes configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by adding your first rate code.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th>Rate Code</th>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => (
                                <tr key={r.id}>
                                    <td>
                                        <div className="pi-profile-cell">
                                            <RateCodeIcon />
                                            <span className="pi-id-cell">{r.rate_code}</span>
                                        </div>
                                    </td>
                                    <td>{r.rate_code_name || <span className="pi-no-value">—</span>}</td>
                                    <td>
                                        <span className={r.active ? 'pi-badge-active' : 'pi-badge-inactive'}>
                                            {r.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="pi-actions">
                                            <button
                                                className="pi-btn-edit"
                                                onClick={() => navigate(`/apps/payment-porter/${ppId}/rate-codes/${r.id}/edit`)}
                                                title="Edit"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="pi-btn-delete"
                                                onClick={() => handleDelete(r.id)}
                                                title="Delete"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default RateCodesPage;
